import { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { uploadImageToCloudflare } from '../../utils/cloudflareUpload';
import { useAuth } from '../../contexts/AuthContext';
import { getAwardEmoji } from '../../standouts/data/standoutsConstants';
import './StandoutShareModal.css';

const StandoutShareModal = ({ standout, cardRef, onCardSaved, onClose }) => {
  const { updateStandoutCardImage, trackStandoutAnalytics } = useAuth();
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const cachedFileRef = useRef(null);

  const shareUrl = `${window.location.origin}/standouts/athlete/${standout.id}`;
  const athleteName = standout.athlete_name || '';
  const award = standout.award || '';
  const sport = standout.sport || '';
  const shareText = `${getAwardEmoji(award, sport)} ${athleteName} was named this week's ${award} Standout!`;
  const fileName = `${athleteName.replace(/\s+/g, '-')}-standout.png`;

  // Generate image File from card DOM
  const generateImageFile = async () => {
    if (!cardRef?.current) return null;
    const cardCanvas = await html2canvas(cardRef.current, {
      useCORS: true,
      scale: 2,
      backgroundColor: '#000000',
      logging: false,
      onclone: (_doc, el) => {
        el.style.webkitMaskImage = 'none';
        el.style.maskImage = 'none';
        el.style.gap = '0';
        el.querySelectorAll('.sd-body, .sd-stats-row, .sd-stat, .sd-location, .sd-photo-wrap, .sd-card-header').forEach(node => {
          node.style.background = '#000000';
        });
        const photoWrap = el.querySelector('.sd-photo-wrap');
        if (photoWrap) photoWrap.style.marginBottom = '-2px';
        const body = el.querySelector('.sd-body');
        if (body) body.style.marginTop = '-2px';
      },
    });

    const cw = cardCanvas.width;
    const ch = cardCanvas.height;
    const pad = Math.round(cw * 0.06);

    let finalW = cw + pad * 2;
    const finalH = ch + pad * 2;
    const minW = Math.ceil(finalH * 0.8);
    if (finalW < minW) finalW = minW;

    const out = document.createElement('canvas');
    out.width = finalW;
    out.height = finalH;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, finalW, finalH);
    ctx.drawImage(cardCanvas, Math.round((finalW - cw) / 2), Math.round((finalH - ch) / 2));

    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png'));
    return new File([blob], fileName, { type: 'image/png' });
  };

  // Upload to Cloudflare + save URL (only if not already saved)
  const ensureCardSaved = async () => {
    if (standout.card_image_url) return standout.card_image_url;
    const file = await generateImageFile();
    if (!file) return null;
    const cardUrl = await uploadImageToCloudflare(file);
    await updateStandoutCardImage(standout.id, cardUrl);
    if (onCardSaved) onCardSaved(cardUrl);
    return cardUrl;
  };

  // ── SHARE button ──
  const handleNativeShare = async () => {
    setSharing(true);
    try {
      const isMobile = navigator.maxTouchPoints > 0;

      if (isMobile && navigator.share) {
        // Mobile (iOS/Android): gesture context expires after html2canvas.
        // Call navigator.share() immediately — no awaits before this line.
        await navigator.share({
          title: `${athleteName} — Standout of the Week`,
          text: shareText,
          url: shareUrl,
        });
        trackStandoutAnalytics(standout.id, 'share');
        ensureCardSaved().catch(() => {});
        return;
      }

      // Desktop: gesture window long enough for html2canvas → share with image file
      const file = await generateImageFile();

      if (file && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${athleteName} — Standout of the Week`,
          text: shareText,
        });
        await ensureCardSaved();
        trackStandoutAnalytics(standout.id, 'share');
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: `${athleteName} — Standout of the Week`,
          text: shareText,
          url: shareUrl,
        });
        trackStandoutAnalytics(standout.id, 'share');
        ensureCardSaved().catch(() => {});
        return;
      }

      // Last fallback: copy link
      await navigator.clipboard.writeText(shareUrl);
      trackStandoutAnalytics(standout.id, 'copy_link');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // user dismissed
    } finally {
      setSharing(false);
    }
  };

  // ── Copy Link ──
  const handleCopy = async () => {
    setSharing(true);
    try {
      await ensureCardSaved();
      await navigator.clipboard.writeText(shareUrl);
      trackStandoutAnalytics(standout.id, 'copy_link');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
    } finally {
      setSharing(false);
    }
  };

  // ── Save Image ──
  // iOS: anchor download saves to Files, not Photos. Use navigator.share with
  // the image file instead — native sheet has "Save Image" → Camera Roll.
  const handleSaveImage = async () => {
    if (!cardRef?.current) return;
    setSaving(true);
    try {
      if (!cachedFileRef.current) {
        cachedFileRef.current = await generateImageFile();
      }
      const file = cachedFileRef.current;
      if (!file) return;

      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

      if (isIOS && navigator.share) {
        try {
          await navigator.share({ files: [file], title: fileName });
        } catch (shareErr) {
          // AbortError = user dismissed sheet — do nothing
          // Anything else = fall back to download
          if (shareErr?.name !== 'AbortError') {
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 500);
          }
        }
      } else {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 500);
      }

      trackStandoutAnalytics(standout.id, 'download');
      ensureCardSaved().catch(() => {});
    } catch (err) {
      console.error('Save image failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ssm-overlay" onClick={onClose}>
      <div className="ssm-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="ssm-header">
          <div>
            <h3 className="ssm-title">Share Standout</h3>
            <p className="ssm-subtitle">Share this athlete's standout and celebrate their achievement.</p>
          </div>
          <button className="ssm-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── Actions ── */}
        <div className="ssm-actions">
          <button className="ssm-share-primary" onClick={handleNativeShare} disabled={sharing}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {sharing ? 'Preparing…' : 'SHARE'}
          </button>

          <button className="ssm-row-btn" onClick={handleSaveImage} disabled={saving || sharing}>
            <span className="ssm-row-label">{saving ? 'Generating…' : 'Save Image'}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>

          <button className="ssm-row-btn" onClick={handleCopy} disabled={sharing}>
            <span className="ssm-row-label">{sharing ? 'Preparing…' : copied ? 'Copied!' : 'Copy Link'}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
        </div>

        {/* ── URL ── */}
        <div className="ssm-url-row">
          <span className="ssm-url-text">{shareUrl}</span>
        </div>

      </div>
    </div>
  );
};

export default StandoutShareModal;
